import { Button, Link, Typography } from '@mui/material';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { ButtonContainer, ModalContent, ModalHeader, ModalText, StyledModal } from '@shared/styles/ModalStyles';
import React, { useContext, useEffect } from 'react';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';

interface ReviewModalProps {
  open: boolean;
  consumedDeepDives: number;
  maxConsumableDeepDivesInSubPeriod: number | null;
  cancelText: string;
  onCancel: () => void;
  onProceed?: () => void;
}

const ReviewUnlockModal: React.FC<ReviewModalProps> = ({
  open,
  consumedDeepDives,
  maxConsumableDeepDivesInSubPeriod,
  onCancel,
  cancelText,
  onProceed,
}) => {
  const isPlanMaxed =
    maxConsumableDeepDivesInSubPeriod !== null && maxConsumableDeepDivesInSubPeriod - consumedDeepDives <= 0;

  const { userLoadState } = useContext(UserContext);

  useEffect(() => {
    if (open && !isPlanMaxed && onProceed) {
      onProceed();
    }
  }, [open, isPlanMaxed, onProceed]);

  if (userLoadState === UserLoadState.None) {
    return <LoadingSpinner variant="circular" size="large" message="Loading Subscription Plan" fullScreen />;
  }

  if (!isPlanMaxed) {
    return null;
  }

  return (
    <StyledModal
      open={open}
      onClose={onCancel}
      aria-labelledby="review-modal-title"
      aria-describedby="review-modal-description"
    >
      <ModalContent>
        {isPlanMaxed && <ModalHeader>No Deep Dive Credits Left</ModalHeader>}

        {!isPlanMaxed ? (
          <ModalText>
            Unlocking this review will leave you with{' '}
            <strong data-testid="post-unlock-count">
              {maxConsumableDeepDivesInSubPeriod - consumedDeepDives - 1} Deal Retro unlocks remaining
            </strong>
            . Do you wish to proceed?
          </ModalText>
        ) : (
          <ModalText>You have reached your plan limit. Check out our pricing page to upgrade to a paid plan.</ModalText>
        )}

        {/* <ProgressLineChart
          consumed={consumedDeepDives}
          total={maxConsumableDeepDivesInSubPeriod}
          subText={consumedDeepDives + '/' + maxConsumableDeepDivesInSubPeriod}
          title="Deal Retros Consumed"
        /> */}

        <ButtonContainer>
          {isPlanMaxed && (
            <Button
              variant="contained"
              onClick={() => window.open('https://www.kandir.io/pricing', '_blank', 'noopener,noreferrer')}
              sx={{
                padding: 'var(--spacing-sm) var(--spacing-md)',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'var(--color-primary-light-blue)',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                '&:hover': {
                  backgroundColor: '#adc7f0',
                },
              }}
            >
              Purchase Credits
            </Button>
          )}
          {!isPlanMaxed && (
            <Button
              variant="contained"
              onClick={onProceed}
              sx={{
                padding: 'var(--spacing-sm) var(--spacing-md)',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'var(--color-primary-light-blue)',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                '&:hover': {
                  backgroundColor: '#adc7f0',
                },
              }}
            >
              Continue to Deal Retro
            </Button>
          )}
        </ButtonContainer>
        <Typography variant="caption" sx={{ mt: 2 }}>
          or{' '}
          <Link onClick={onCancel} sx={{ cursor: 'pointer' }}>
            {cancelText}
          </Link>
        </Typography>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Want to unlock more Deep Dives? For each Deep Dive you submit we'll add one additional review to consume to
          your account. Or you can go to a{' '}
          <Link href="https://www.kandir.io/pricing" variant="body2" target="_blank">
            paid plan
          </Link>
          .
        </Typography>
      </ModalContent>
    </StyledModal>
  );
};

export default ReviewUnlockModal;
